import { type ApiContainerAction, type ApiOrder, EnumContainerOrderAction, isBooked } from '@Model';
import { renderDateTime } from '@synqo/frontend-component-library';
import { ACTION_NOUN } from './containerActionKind';
import { lineState } from './lineConfirmation';
import { kindOfRow, takesAway } from './siteMatching';

/** One line in the correction banner. */
export interface ContainerCorrection {
    subject: string;
    change: string;
    effect: string;
}

type Translate = (key: string) => string;

/** A line whose effect still depends on how many equal lines are counted together. */
interface CountedCorrection {
    subject: string;
    change: string;
    effect: (count: number) => string;
}

/** The containers, completion date and place of an order, at the start of editing or now. */
export interface ContainerCorrectionState {
    actions: readonly ApiContainerAction[];
    completedAt: string | undefined;
    /** The customer and site the bookings are tied to, see {@link placeOf}. */
    place: string;
}

/** A key that changes whenever the order moves to another customer or site. */
// @ai-generated 2026-10-28T08:53:02+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function placeOf(order: ApiOrder): string {
    return `${order.customer.foreign_key ?? ''}|${order.site.address.foreign_key ?? ''}`;
}

/**
 * Lists what saving changes about the booked containers: removed, renumbered, rebooked and added container
 * actions, a moved completion date and another customer or site. Container actions are matched by id, so both
 * states must come from the same edit.
 */
// @ai-generated 2026-10-19T08:21:57+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function containerCorrections(
    booked: ContainerCorrectionState,
    current: ContainerCorrectionState,
    i18n: Translate,
): ContainerCorrection[] {
    const now = new Map(current.actions.map((action) => [action.id, action]));
    const before = new Set(booked.actions.map((action) => action.id));
    const replacements = replacementsOf(booked.actions, current.actions);
    const replacing = new Set([...replacements.values()].map((action) => action.id));
    const corrections: CountedCorrection[] = [];

    for (const action of booked.actions.filter((candidate) => isBooked(candidate.booking_status))) {
        const after = now.get(action.id);
        const replacement = replacements.get(action.id);
        if (replacement) {
            corrections.push(replacedBy(action, replacement, current.completedAt, i18n));
        } else if (!after && !isExchangeDelivery(action)) {
            corrections.push(removal(action, booked.actions, i18n));
        } else if (after && changesTheService(action, after)) {
            corrections.push(serviceChange(action, after, i18n));
        } else if (after?.container.foreign_key && after.container.foreign_key !== action.container.foreign_key) {
            corrections.push(renumbering(action, after, current.completedAt, i18n));
        }
    }

    for (const action of current.actions.filter(
        (candidate) => !before.has(candidate.id) && !isExchangeDelivery(candidate) && !replacing.has(candidate.id),
    )) {
        corrections.push(addition(action, i18n));
    }

    if (
        booked.completedAt &&
        current.completedAt &&
        Date.parse(booked.completedAt) !== Date.parse(current.completedAt)
    ) {
        const withinTheDay = renderDateTime(booked.completedAt, 'date') === renderDateTime(current.completedAt, 'date');
        const mode = withinTheDay ? 'datetime' : 'date';
        const movedTo = renderDateTime(current.completedAt, mode);
        corrections.push({
            subject: i18n('Erledigt am'),
            change: `${renderDateTime(booked.completedAt, mode)} → ${movedTo}`,
            effect: () => fill(i18n('alle Buchungen auf {date}'), { date: movedTo }),
        });
    }

    if (
        booked.place !== current.place &&
        booked.actions.some((action) => isBooked(action.booking_status) && now.has(action.id))
    ) {
        corrections.push({
            subject: i18n('Kunde / Lieferanschrift'),
            change: i18n('geändert'),
            effect: () => i18n('alle Buchungen werden storniert und neu gebucht'),
        });
    }

    return mergeRepeated(corrections);
}

/** The placement added for a removed one, by the removed one's id, where each is the only one of its type. */
// @ai-generated 2026-10-30T10:13:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function replacementsOf(
    booked: readonly ApiContainerAction[],
    current: readonly ApiContainerAction[],
): Map<string, ApiContainerAction> {
    const isPlainPlacement = (action: ApiContainerAction) =>
        action.action === EnumContainerOrderAction.Place && !action.exchange_id;
    const removed = booked.filter(
        (action) =>
            isPlainPlacement(action) &&
            isBooked(action.booking_status) &&
            !current.some((other) => other.id === action.id),
    );
    const added = current.filter(
        (action) =>
            isPlainPlacement(action) &&
            !!action.container.foreign_key &&
            !booked.some((other) => other.id === action.id),
    );
    const ofType = (actions: ApiContainerAction[], type: string | undefined) =>
        actions.filter((action) => action.container_type.foreign_key === type);

    const replacements = new Map<string, ApiContainerAction>();
    for (const action of removed) {
        const [removedOfType, addedOfType] = [removed, added].map((actions) =>
            ofType(actions, action.container_type.foreign_key),
        );
        if (removedOfType.length === 1 && addedOfType.length === 1) {
            replacements.set(action.id, addedOfType[0]);
        }
    }

    return replacements;
}

// @ai-generated 2026-10-30T10:13:24+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function replacedBy(
    removed: ApiContainerAction,
    added: ApiContainerAction,
    completedAt: string | undefined,
    i18n: Translate,
): CountedCorrection {
    const line = renumbering(removed, added, completedAt, i18n);
    const change =
        removed.content.foreign_key === added.content.foreign_key
            ? line.change
            : `${line.change}, ${removed.content.display_string || '—'} → ${added.content.display_string || '—'}`;

    return { ...line, change };
}

function isExchangeDelivery(action: ApiContainerAction): boolean {
    return Boolean(action.exchange_id) && !takesAway(action);
}

// @ai-generated 2026-10-28T14:15:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function removal(
    action: ApiContainerAction,
    booked: readonly ApiContainerAction[],
    i18n: Translate,
): CountedCorrection {
    const subject = action.container_type.display_string;
    const change = fill(i18n('{action} entfernt'), { action: i18n(ACTION_NOUN[kindOfRow(action)]) });
    const stays = (count: number) =>
        fill(i18n(count === 1 ? '{container} bleibt stehen' : '{container} bleiben stehen'), {
            container: labelOf(action, count, i18n),
        });
    const cancelled = (cancelledAction: ApiContainerAction | undefined, count: number) =>
        fill(i18n(count === 1 ? '{container} wird storniert' : '{container} werden storniert'), {
            container: labelOf(cancelledAction, count, i18n),
        });

    if (action.exchange_id) {
        const delivery = booked.find((other) => other.exchange_id === action.exchange_id && other.id !== action.id);
        const swapped = (count: number) =>
            fill(
                i18n(
                    count === 1
                        ? '{pickup} bleibt stehen, {delivery} wird storniert'
                        : '{pickup} bleiben stehen, {delivery} werden storniert',
                ),
                { pickup: labelOf(action, count, i18n), delivery: labelOf(delivery, count, i18n) },
            );
        return { subject, change, effect: swapped };
    }

    switch (action.action) {
        case EnumContainerOrderAction.Place:
            return { subject, change, effect: (count) => cancelled(action, count) };
        case EnumContainerOrderAction.Collect:
            return { subject, change, effect: stays };
        default:
            return { subject, change, effect: () => i18n('Bestand bleibt gleich') };
    }
}

// @ai-generated 2026-10-28T14:15:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function renumbering(
    before: ApiContainerAction,
    after: ApiContainerAction,
    completedAt: string | undefined,
    i18n: Translate,
): CountedCorrection {
    const [from, to] = [labelOf(before, 1, i18n), labelOf(after, 1, i18n)];
    const subject = before.container_type.display_string;
    const change = `${from} → ${to}`;
    const open = lineState(after.booking_status) === 'open';

    switch (before.action) {
        case EnumContainerOrderAction.Place: {
            const stands = open
                ? fill(i18n('{from} wird storniert, {to} muss bestätigt werden'), { from, to })
                : completedAt
                  ? fill(i18n('{from} wird storniert, {to} steht ab {date}'), {
                        from,
                        to,
                        date: renderDateTime(completedAt, 'date'),
                    })
                  : fill(i18n('{from} wird storniert, {to} steht'), { from, to });
            return { subject, change, effect: () => stands };
        }
        case EnumContainerOrderAction.Collect: {
            const text = open
                ? '{from} bleibt stehen, {to} muss bestätigt werden'
                : '{from} bleibt stehen, {to} wird abgeholt';
            return { subject, change, effect: () => fill(i18n(text), { from, to }) };
        }
        default: {
            const text = open ? '{to} muss bestätigt werden' : '{to} wird statt {from} geleert';
            return { subject, change, effect: () => fill(i18n(text), { from, to }) };
        }
    }
}

function changesTheService(before: ApiContainerAction, after: ApiContainerAction): boolean {
    return (
        before.container_type.foreign_key !== after.container_type.foreign_key ||
        before.content.foreign_key !== after.content.foreign_key
    );
}

// @ai-generated 2026-10-28T10:14:45+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function serviceChange(before: ApiContainerAction, after: ApiContainerAction, i18n: Translate): CountedCorrection {
    const [from, to] =
        before.container_type.foreign_key !== after.container_type.foreign_key
            ? [before.container_type.display_string, after.container_type.display_string]
            : [before.content.display_string, after.content.display_string];
    const effect =
        lineState(after.booking_status) === 'open'
            ? 'wird storniert, muss bestätigt werden'
            : 'wird storniert und neu gebucht';
    return {
        subject: before.container_type.display_string,
        change: `${from || '—'} → ${to || '—'}`,
        effect: () => i18n(effect),
    };
}

// @ai-generated 2026-10-19T08:21:57+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function addition(action: ApiContainerAction, i18n: Translate): CountedCorrection {
    const effect = lineState(action.booking_status) === 'open' ? 'muss bestätigt werden' : 'wird mit Speichern gebucht';
    return {
        subject: action.container_type.display_string,
        change: fill(i18n('{action} ergänzt'), { action: i18n(ACTION_NOUN[kindOfRow(action)]) }),
        effect: () => i18n(effect),
    };
}

function labelOf(action: ApiContainerAction | undefined, count: number, i18n: Translate): string {
    return action?.container.display_string
        ? fill(i18n('Nr. {number}'), { number: action.container.display_string })
        : fill(i18n('{count} Container'), { count: String(count) });
}

/** Puts the values into the `{name}` placeholders of a translated text, in one pass. */
// @ai-generated 2026-10-28T15:09:35+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function fill(text: string, values: Record<string, string>): string {
    return text.replace(/\{(\w+)\}/g, (placeholder, name: string) => values[name] ?? placeholder);
}

// @ai-generated 2026-10-28T14:15:12+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function mergeRepeated(corrections: CountedCorrection[]): ContainerCorrection[] {
    const counted = new Map<string, { correction: CountedCorrection; count: number }>();
    for (const correction of corrections) {
        const key = `${correction.subject}|${correction.change}|${correction.effect(1)}`;
        counted.set(key, { correction, count: (counted.get(key)?.count ?? 0) + 1 });
    }

    return [...counted.values()].map(({ correction, count }) => ({
        subject: correction.subject,
        change: count === 1 ? correction.change : `${count} × ${correction.change}`,
        effect: correction.effect(count),
    }));
}
